import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Stack,
  Table,
  Text,
  Button,
  Group,
  Loader,
  Center,
  Paper,
  Alert,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconAlertCircle, IconPlus } from "@tabler/icons-react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getEligibilityRules,
  replaceEligibilityRules,
  EligibilityRuleRequest,
} from "../../../services/prefixService";
import EligibilityRow from "./EligibilityRow";
import AddEligibilityRuleModal from "./AddEligibilityRuleModal";
import { EligibilityRuleDraft } from "./AddEligibilityRuleForm";

interface PrefixInsuranceEligibilityPanelProps {
  prefixId: string;
}

function classifyEligibilityError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 400) {
      return "Some eligibility rules are invalid. Please review and try again.";
    }
    if (error.response?.status === 403) {
      return "You don't have permission to change eligibility rules.";
    }
  }
  return "Unable to save eligibility rules. Please try again.";
}

export default function PrefixInsuranceEligibilityPanel({
  prefixId,
}: PrefixInsuranceEligibilityPanelProps) {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<EligibilityRuleDraft[]>([]);
  const [addModalOpened, { open: openAddModal, close: closeAddModal }] =
    useDisclosure(false);

  const { data: rules, isLoading: loadingRules } = useQuery({
    queryKey: ["eligibility", prefixId],
    queryFn: () => getEligibilityRules(prefixId),
  });

  // Sync server rules into local state on load / refetch.
  useEffect(() => {
    if (rules) {
      setRows(
        rules.map((r) => ({
          key: r.id,
          insuranceProviderId: r.insuranceProviderId,
          insuranceProviderName:
            r.insuranceProviderName ?? r.insuranceProvider?.name ?? null,
          isCatchAll: r.isCatchAll,
          tcpcEligible: r.tcpcEligible,
        }))
      );
    }
  }, [rules]);

  const persistMutation = useMutation({
    mutationFn: (requests: EligibilityRuleRequest[]) =>
      replaceEligibilityRules(prefixId, requests),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eligibility", prefixId] });
    },
    onError: () => {
      // Revert optimistic local edits to the last known server state.
      queryClient.invalidateQueries({ queryKey: ["eligibility", prefixId] });
    },
  });

  const persist = useCallback(
    (nextRows: EligibilityRuleDraft[]) => {
      const requests: EligibilityRuleRequest[] = nextRows.map((r) => ({
        insuranceProviderId: r.insuranceProviderId,
        isCatchAll: r.isCatchAll,
        tcpcEligible: r.tcpcEligible,
      }));
      persistMutation.mutate(requests);
    },
    [persistMutation]
  );

  const handleToggle = useCallback(
    (rowKey: string) => {
      const next = rows.map((r) =>
        r.key === rowKey ? { ...r, tcpcEligible: !r.tcpcEligible } : r
      );
      setRows(next);
      persist(next);
    },
    [rows, persist]
  );

  const handleRemove = useCallback(
    (rowKey: string) => {
      const next = rows.filter((r) => r.key !== rowKey);
      setRows(next);
      persist(next);
    },
    [rows, persist]
  );

  const handleAddRow = useCallback(
    (draft: EligibilityRuleDraft) => {
      const specifics = rows.filter((r) => !r.isCatchAll);
      const catchAll = rows.find((r) => r.isCatchAll);
      const next = draft.isCatchAll
        ? [...specifics, draft]
        : [...specifics, draft, ...(catchAll ? [catchAll] : [])];
      setRows(next);
      persist(next);
    },
    [rows, persist]
  );

  const orderedRows = useMemo(
    () => [
      ...rows.filter((r) => !r.isCatchAll),
      ...rows.filter((r) => r.isCatchAll),
    ],
    [rows]
  );

  const existingProviderIds = useMemo(
    () =>
      rows
        .filter((r) => !r.isCatchAll && r.insuranceProviderId)
        .map((r) => r.insuranceProviderId as string),
    [rows]
  );

  const hasCatchAll = useMemo(() => rows.some((r) => r.isCatchAll), [rows]);

  const errorMessage = useMemo(
    () =>
      persistMutation.error
        ? classifyEligibilityError(persistMutation.error)
        : null,
    [persistMutation.error]
  );

  if (loadingRules) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      {errorMessage && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          title="Couldn't save eligibility rules"
          role="alert"
        >
          {errorMessage}
        </Alert>
      )}

      <Group justify="space-between">
        <Text fw={500} size="sm">
          Insurance Eligibility
        </Text>
        <Button
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={openAddModal}
          disabled={persistMutation.isPending}
        >
          Add Rule
        </Button>
      </Group>

      <Paper withBorder style={{ overflow: "hidden" }}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Insurance Carrier</Table.Th>
              <Table.Th>TCPC Eligible</Table.Th>
              <Table.Th style={{ width: 60 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {orderedRows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={3}>
                  <Text size="sm" c="dimmed" ta="center" py="sm">
                    No eligibility rules. Click Add Rule to create one.
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              orderedRows.map((row) => (
                <EligibilityRow
                  key={row.key}
                  rowKey={row.key}
                  insuranceProviderName={row.insuranceProviderName}
                  insuranceProviderId={row.insuranceProviderId}
                  isCatchAll={row.isCatchAll}
                  tcpcEligible={row.tcpcEligible}
                  disabled={persistMutation.isPending}
                  onToggle={handleToggle}
                  onRemove={handleRemove}
                />
              ))
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      <AddEligibilityRuleModal
        opened={addModalOpened}
        onClose={closeAddModal}
        existingProviderIds={existingProviderIds}
        hasCatchAll={hasCatchAll}
        onConfirm={handleAddRow}
      />
    </Stack>
  );
}
